import React from "react";
import { Flame, ShieldCheck, Wrench, PhoneCall, MapPin, Clock, CircleCheck as CheckCircle2, Sparkles, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function NavienHero() {
  const features = [{
    title: "Experienced Navien Techs",
    desc: "Trained diagnostics for NPE, NPN, and NCB series units."
  }, {
    title: "24/7 Emergency Response",
    desc: "Fast dispatch across Jonesboro and surrounding areas."
  }, {
    title: "High-Efficiency Guarantee",
    desc: "Up to 0.96 UEF condensing tankless installations."
  }];
  const specs = [{
    label: "Efficiency",
    value: "Up to 98% Thermal"
  }, {
    label: "Response Time",
    value: "Under 60 Mins"
  }, {
    label: "Warranty Coverage",
    value: "100% Guaranteed"
  }];
  return <section id="navienhero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 lg:py-28 border-b border-border">
      {/* Decorative Brand Accent Grid Background */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-accent/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Core Value Proposition */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-xs tracking-wide uppercase">
                <Flame className="size-3.5 text-accent mr-1" />
                Navien Certified Water Heater Service
              </Badge>
              <Badge variant="outline" className="text-xs bg-background/80">
                <Clock className="size-3 text-muted-foreground mr-1" />
                Jonesboro Emergency Dispatch
              </Badge>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
              Expert Navien Tankless & Combi-Boiler Solutions in Jonesboro
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Restore endless hot water fast. Jonesboro Emergency Plumber delivers 24/7 emergency repair, precision maintenance, and professional installation for all residential and commercial Navien systems.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="shadow-md font-medium text-base">
                <Link to="/contact/">
                  <PhoneCall className="mr-2 size-4" />
                  Schedule Navien Service
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-medium text-base">
                <Link to="/service-areas/">
                  <MapPin className="mr-2 size-4 text-muted-foreground" />
                  View Service Areas
                </Link>
              </Button>
            </div>

            {/* Bullet Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full pt-4 border-t border-border/80">
              {features.map((item, i) => <div data-index={i} key={item.title} className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5 text-foreground font-semibold text-sm">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>{item.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground pl-5.5">
                    {item.desc}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Right Column: Visual Brand Card / Imagery */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Card className="relative overflow-hidden border border-border bg-card shadow-lg">
              {/* Product / Visual Showcase */}
              <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/navien_tankless_water_heater_unit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/navien_tankless_water_heater_unit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/navien_tankless_water_heater_unit.png-wvc-sizes" alt="High efficiency Navien condensing tankless water heater installed in Jonesboro" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold px-2.5 py-1 shadow-sm">
                    <ShieldCheck className="size-3.5" />
                    Authorized Navien Service
                  </span>
                </div>
                <div className="absolute bottom-3 right-3">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-background/90 text-foreground backdrop-blur-xs text-xs font-medium px-2.5 py-1 border border-border shadow-xs">
                    <Zap className="size-3.5 text-accent" />
                    Condensing Technology
                  </span>
                </div>
              </div>

              {/* Card Footer / Specs Panel */}
              <CardContent className="p-5 flex flex-col gap-4">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-md bg-secondary text-secondary-foreground">
                      <Wrench className="size-4" />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold font-default text-foreground">
                        Navien Unit Maintenance
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        Descaling, Error Code Diagnostics & Tuning
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="font-semibold text-xs">
                    NPE-A2 / S2 Series
                  </Badge>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center">
                  {specs.map((spec, i) => <div data-index={i} key={spec.label} className="bg-muted/50 rounded-md p-2 border border-border/50">
                      <div className="text-xs font-bold text-foreground">
                        {spec.value}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {spec.label}
                      </div>
                    </div>)}
                </div>

                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Sparkles className="size-3.5 text-accent" />
                    Same-day service available
                  </span>
                  <Link to="/tankless-water-heater-service/" className="text-xs font-medium text-primary hover:underline">
                    Explore Tankless Services →
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}